import type { VolumeItem } from '@/delivery-type-plugin/capabilities/container-storage';
import type { MaasSubmitError } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { readModelInstanceFormValues } from '@/delivery-type-plugin/capabilities/maas-model-storage/modelFormUtils';
import type { ModelInstanceFormValues, ModelInstanceSpec } from '@/delivery-type-plugin/capabilities/maas-model-storage/modelInstanceTypes';
import {
  collectBaselineNodesFromInitValues,
  validateMaasGroupedGpuUsageLatest,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/gpuResourceValidation';
import {
  validateEmbeddedNodesSystemDiskForSubmit,
  validateMaasGroupedResourcesForSubmit,
  validateMaasRunModeTopology,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/submitValidation';

import { findDuplicateTrainingSharedVolumeMountPath } from './trainingClusterSpec';

/** 训练集群 flatGroups spec 拓扑与资源校验（不含 inference / 网关 / 服务） */
export async function getTrainingClusterSubmitError(
  values: ModelInstanceFormValues,
  spec: ModelInstanceSpec,
  options?: {
    initValues?: ModelInstanceFormValues;
    /** 直建页共享存储开关关闭时跳过 mountPath 重复校验 */
    skipSharedStorageValidation?: boolean;
    runtimeTypeOnly?: boolean;
    /** SKU 资源配置步/预置：无租户空间与实例名称字段 */
    skipTenantAndNameValidation?: boolean;
  },
): Promise<MaasSubmitError | undefined> {
  if (!options?.skipTenantAndNameValidation) {
    if (!values.workspace?.trim()) {
      return { key: 'common:validate.required.select', fieldKey: 'common:tenant.space' };
    }
    if (!values.alias?.trim()) {
      return { key: 'common:validate.required', fieldKey: 'common:name' };
    }
  }
  if (!options?.skipTenantAndNameValidation) {
    if (!values.runtime?.type || (!options?.runtimeTypeOnly && !values.runtime?.workload)) {
      return { key: 'common:validate.required.select', fieldKey: 'data-center:maas.runtime.type' };
    }
  }

  const topologyError = validateMaasRunModeTopology({
    runMode: values.runMode,
    headGroupSpec: spec.headGroupSpec,
    workerGroupSpecs: spec.workerGroupSpecs,
  });
  if (topologyError) return topologyError;

  const systemDiskError = validateEmbeddedNodesSystemDiskForSubmit(values);
  if (systemDiskError) return systemDiskError;

  const resourceError = validateMaasGroupedResourcesForSubmit({
    headGroupSpec: spec.headGroupSpec,
    workerGroupSpecs: spec.workerGroupSpecs,
  });
  if (resourceError) return resourceError;

  if (!options?.skipSharedStorageValidation) {
    const raw = values as Record<string, unknown>;
    const enabled = raw.trainingSharedStorageEnabled === true;
    const volumes = raw.trainingSharedVolumes as VolumeItem[] | undefined;
    if (enabled && volumes?.length) {
      const duplicate = findDuplicateTrainingSharedVolumeMountPath(volumes);
      if (duplicate) {
        return {
          key: 'data-center:training.cluster.shared.volume.mount.path.duplicate',
          params: { path: duplicate },
        };
      }
    }
  }

  const baselineNodes = options?.initValues ? collectBaselineNodesFromInitValues(options.initValues) : undefined;
  return validateMaasGroupedGpuUsageLatest(
    {
      headGroupSpec: spec.headGroupSpec,
      workerGroupSpecs: spec.workerGroupSpecs,
    },
    { baselineNodes },
  );
}

export function readTrainingClusterFormValues(form: Parameters<typeof readModelInstanceFormValues>[0]): ModelInstanceFormValues {
  return readModelInstanceFormValues(form);
}
